import { readTokens, rememberTokens, clearTokens } from '../auth/tokens';
const PRODUCTION_API_BASE_URL = 'https://serv-beaw-ai-backend-684687736819.asia-southeast1.run.app/api';
const isLoopbackHost = (hostname: string) => ['localhost', '127.0.0.1', '0.0.0.0'].includes(hostname);
const rawBase = window.__BEAW_CONFIG__?.API_BASE_URL || import.meta.env.VITE_API_BASE_URL || (import.meta.env.DEV ? 'http://localhost:8888/api' : PRODUCTION_API_BASE_URL);
const configuredBase = rawBase.replace(/\/+$/, '');
const configuredHost = (() => { try { return new URL(configuredBase, window.location.origin).hostname; } catch { return ''; } })();
// Never let a deployed website call localhost on the visitor's phone or PC,
// even when an old Cloud Run environment variable is still present.
export const CONFIGURED_API_BASE_URL = !isLoopbackHost(window.location.hostname) && isLoopbackHost(configuredHost)
  ? PRODUCTION_API_BASE_URL
  : configuredBase;
export const API_BASE_URL = import.meta.env.DEV && /^https?:\/\//.test(CONFIGURED_API_BASE_URL)
  ? `${window.location.origin}/backend-api`
  : CONFIGURED_API_BASE_URL;

function backendUnavailableMessage() {
  return 'ขณะนี้ระบบเชื่อมต่อไม่สำเร็จ กรุณารอสักครู่แล้วลองอีกครั้งครับ';
}

function apiUrl(path: string) {
  const cleanPath = path.replace(/^\/api\//, '/').replace(/^([^/])/, '/$1');
  return new URL(`${API_BASE_URL}${cleanPath}`, window.location.origin);
}

function isAbortError(reason: unknown): reason is Error {
  return reason instanceof Error && reason.name === 'AbortError';
}

export class ApiError extends Error {
  status: number;
  code: string;
  retryAfter: number;
  constructor(status: number, code: string, message: string, retryAfter = 0) {
    super(message); this.name = 'ApiError'; this.status = status; this.code = code; this.retryAfter = retryAfter;
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & { body?: unknown; query?: Record<string, string | number | boolean | undefined | null>; authRetried?: boolean };

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = apiUrl(path);
  Object.entries(options.query || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
  });
  let response: Response;
  const multipart = options.body instanceof FormData;
  const requestTokens = readTokens();
  try {
    response = await fetch(url, {
      ...options,
      headers: { Accept: 'application/json', ...(requestTokens?.access_token ? { Authorization: `Bearer ${requestTokens.access_token}` } : {}), ...(options.body !== undefined && !multipart ? { 'Content-Type': 'application/json' } : {}), ...options.headers },
      body: options.body === undefined ? undefined : multipart ? options.body as FormData : JSON.stringify(options.body),
    });
  } catch (reason) {
    if (isAbortError(reason)) throw reason;
    console.error('[beaw-api] request failed', { path: url.pathname, url: url.toString(), reason });
    throw new ApiError(0, 'BACKEND_UNREACHABLE', backendUnavailableMessage());
  }
  if (response.status === 401 && !options.authRetried && readTokens()?.refresh_token && !path.includes('/auth/v2/refresh')) {
    if (!refreshingTokens) refreshingTokens = refreshSessionTokens(requestTokens?.access_token).finally(() => { refreshingTokens = null; });
    if (await refreshingTokens) return apiRequest<T>(path, { ...options, authRetried: true });
  }
  let payload: unknown;
  try { payload = await response.json(); } catch { payload = null; }
  const envelope = payload as { success?: boolean; data?: T; detail?: { error?: { code?: string; message?: string; retry_after?: number } }; error?: { code?: string; message?: string; retry_after?: number } } | null;
  if (!response.ok || envelope?.success === false) {
    const error = envelope?.detail?.error || envelope?.error;
    const fallback = response.status >= 500
      ? backendUnavailableMessage()
      : 'ทำรายการไม่สำเร็จ กรุณาตรวจสอบข้อมูลแล้วลองอีกครั้งครับ';
    if (!error?.message) console.error('[beaw-api] request rejected', { path: url.pathname, status: response.status, payload });
    throw new ApiError(response.status, error?.code || 'REQUEST_FAILED', error?.message || fallback, Number(error?.retry_after || response.headers.get('Retry-After') || 0));
  }
  return (envelope && 'data' in envelope ? envelope.data : payload) as T;
}

export async function apiUploadFile(file: File, folder: string): Promise<{ public_url?: string; signed_read_url?: string; key?: string }> {
  const form = new FormData(); form.append('file', file); form.append('folder', folder);
  let response: Response;
  try { response = await fetch(apiUrl('/r2/upload'), { method: 'POST', headers: { Accept: 'application/json', ...(readTokens()?.access_token ? { Authorization: `Bearer ${readTokens()!.access_token}` } : {}) }, body: form }); }
  catch (reason) {
    if (isAbortError(reason)) throw reason;
    console.error('[beaw-api] upload failed', { path: '/r2/upload', reason });
    throw new ApiError(0, 'BACKEND_UNREACHABLE', backendUnavailableMessage());
  }
  let payload: unknown; try { payload = await response.json(); } catch { payload = null; }
  const envelope = payload as { success?: boolean; data?: { public_url?: string; signed_read_url?: string; key?: string }; detail?: { error?: { code?: string; message?: string } }; error?: { code?: string; message?: string } } | null;
  if (!response.ok || envelope?.success === false) { const error = envelope?.detail?.error || envelope?.error; throw new ApiError(response.status, error?.code || 'UPLOAD_FAILED', error?.message || `Upload failed (${response.status})`); }
  return envelope?.data || {};
}

export function queryString(values: Record<string, string | number | undefined>) {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => value !== undefined && value !== '' && params.set(key, String(value)));
  return params.toString();
}

let refreshingTokens: Promise<boolean> | null = null;
async function refreshSessionTokens(expectedAccess?: string): Promise<boolean> {
  // Rotation must be serialized across tabs, not only requests in one tab.
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request('beaw.auth.refresh', () => performTokenRefresh(expectedAccess));
  }
  return performTokenRefresh(expectedAccess);
}
async function performTokenRefresh(expectedAccess?: string): Promise<boolean> {
  const current = readTokens();
  if (!current) return false;
  if (expectedAccess && current.access_token !== expectedAccess) return true;
  const requestedToken = current.refresh_token;
  try {
    const response = await fetch(apiUrl('/auth/v2/refresh'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refresh_token: requestedToken }) });
    if (readTokens()?.refresh_token !== requestedToken) return Boolean(readTokens());
    if (!response.ok) {
      if (response.status === 401) { clearTokens(); window.dispatchEvent(new Event('beaw-auth-expired')); }
      if (response.status >= 500) throw new ApiError(response.status, 'SESSION_REFRESH_UNAVAILABLE', backendUnavailableMessage());
      return false;
    }
    const result = await response.json();
    if (!result.data?.access_token || !result.data?.refresh_token) throw new ApiError(502, 'SESSION_REFRESH_UNAVAILABLE', backendUnavailableMessage());
    rememberTokens(result.data); return true;
  } catch (reason) {
    if (reason instanceof ApiError) throw reason;
    throw new ApiError(0, 'SESSION_REFRESH_UNAVAILABLE', backendUnavailableMessage());
  }
}
